import React from 'react';
import { motion } from 'framer-motion';
import { Logo } from '../Logo';
import { ThemeToggle } from '../ThemeToggle';
import { DownloadIcon } from 'lucide-react';
import { cvHref, navItems, profile, socialLinks } from '../../data/profile';
import { INDICATOR_SPRING } from '../../utils/motion';
import { useActiveSection } from '../../hooks/useActiveSection';
import { trackEvent } from '../../utils/analytics';
const SECTION_IDS = navItems.map(item => item.id);

// Tracks the active section itself, so scrolling re-renders only the sidebar, not the whole page.
export function Sidebar() {
  const active = useActiveSection(SECTION_IDS);
  const goTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <aside className="sticky top-0 hidden h-screen w-48 shrink-0 flex-col py-10 lg:flex" data-id="element-372" data-mp="383dmwnl14ohl">
      <div className="flex items-center gap-3" data-id="element-373" data-mp="33cxan6sy38wt">
        <Logo className="h-10 w-10" data-id="element-374" data-mp="33c5d3qfsf13w" />
        <div data-id="element-375" data-mp="33b2kne2tgqlv">
          <p className="text-sm font-semibold text-fg" data-id="element-376" data-mp="39p7ds7s72trf">{profile.name}</p>
          <p className="text-xs text-fg-faint" data-id="element-377" data-mp="39r0kto6pzfzy">{profile.location}</p>
        </div>
        <ThemeToggle className="ml-auto" data-id="element-378" data-mp="3enipko21veca" />
      </div>

      <nav aria-label="Sections" className="mt-10" data-id="element-379" data-mp="30x5fog5llpkb">
        <ul className="space-y-0.5" data-id="element-380" data-mp="30wdl98nqlxvm">
          {navItems.map(item => {
          const isActive = active === item.id;
          return <li key={item.id} data-id="element-381" data-mp="2qxwotw614r26">
                <button type="button" onClick={() => goTo(item.id)} aria-current={isActive ? 'true' : undefined} className={`relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 ${isActive ? 'text-ink' : 'text-fg-muted hover:text-fg'}`} data-id="element-382" data-mp="2qwv1otbqoe43">
                  {isActive && <motion.span layoutId="nav-active" className="absolute inset-0 rounded-lg bg-ink-soft" transition={INDICATOR_SPRING} data-id="element-383" data-mp="k5nir1b3hb4e" />}
                  <span className="relative w-4 text-[11px] tabular-nums text-fg-faint" data-id="element-384" data-mp="1k59za1grtrd8">{item.index}</span>
                  <span className="relative font-medium" data-id="element-385" data-mp="1f672t5euwtgv">{item.label}</span>
                </button>
              </li>;
        })}
        </ul>
      </nav>

      <ul className="mt-8 space-y-2 px-3" data-id="element-386" data-mp="27u31gfnzkvga">
        {socialLinks.map(link => <li key={link.label} data-id="element-387" data-mp="2334gv3vytb78">
            <a href={link.href} target="_blank" rel="noreferrer" className="text-xs text-fg-muted underline decoration-line underline-offset-4 transition-colors duration-150 hover:text-ink hover:decoration-ink" data-id="element-388" data-mp="232b5fpm29xir">
              {link.label}
            </a>
          </li>)}
      </ul>

      <a href={cvHref} target={profile.cv ? '_blank' : undefined} rel="noreferrer" onClick={() => trackEvent('cv_download', {
      source: 'sidebar'
    })} className="mt-8 inline-flex items-center justify-center gap-2 rounded-full border border-line bg-white px-5 py-2.5 text-sm font-medium text-fg transition-[border-color,transform] duration-150 ease-out hover:border-ink/40 active:scale-[0.97]" data-id="element-389" data-mp="2qqucy2xae69s">
        <DownloadIcon className="h-4 w-4" aria-hidden data-id="element-390" data-mp="3h3hb1jiwxf60" />
        Download CV
      </a>

      <a href={`mailto:${profile.email}`} className="mt-2 inline-flex items-center justify-center rounded-full bg-ink px-5 py-2.5 text-sm font-medium text-white transition-[background-color,transform] duration-150 ease-out hover:bg-ink-deep active:scale-[0.97]" data-id="element-391" data-mp="3m192ydjk51a7">
        Let's talk
      </a>
    </aside>;
}